import React, { Fragment, useCallback, useEffect, useMemo, useState, useContext } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import axios from "axios";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Grid2 from "@mui/material/Grid2";
import Link from "@mui/material/Link";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Alert from "../../layout/Alert";
import ResponsiveContentWrapper from "../../layout/ResponsiveContentWrapper";
import { connect } from "react-redux";
import { addMfaDevice, getMfaDevices, removeMfaDevice, enableMFA, disableMFA, prepareMfaDeviceVerification } from "../../../action/auth";
import { setAlert } from "../../../action/alert";
import ChangePassword from "./changePassword";
import { UserContext } from "../../routing/Routes";
import AppButton from "../../Common/Button/Button";
import IconButton from "../../Common/Button/IconButton";
import SwitchButton from "../../Common/Button/SwitchButton";
import Dialog from "../../Common/Dailog/Dailog";
import FormInput from "../../Common/FormInputs/MuiTextField";
import env from "../../../env";
import customConfig from "../../../config";
import { generateUpdateProfileOtp, validateUpdateProfileOtp } from "../../../action/password";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import MfaQrCode from "../../auth/mfaQRcode";

const CONTACT_ACTION_LINK_SX = { fontSize: 12 };
const E164_MOBILE_REGEX = /^\+[1-9]\d{3,14}$/;
const IN_LOCAL_MOBILE_REGEX = /^[6-9]\d{9}$/;
const IN_WITH_COUNTRY_NO_PLUS_REGEX = /^91[6-9]\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const hasContactValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const getContactDisplay = (value) =>
  hasContactValue(value) ? String(value).trim() : "Not added";

/** Accept 10-digit India / 91… / +91… and normalize to E.164 before OTP APIs. */
const normalizeMobile = (input) => {
  const stripped = String(input || "")
    .trim()
    .replace(/[\s()-]/g, "");
  if (!stripped) return "";
  if (stripped.startsWith("+")) return stripped;
  if (IN_WITH_COUNTRY_NO_PLUS_REGEX.test(stripped)) return `+${stripped}`;
  if (IN_LOCAL_MOBILE_REGEX.test(stripped)) return `+91${stripped}`;
  return stripped;
};

const validateContactInput = (type, input) => {
  const raw = String(input || "").trim();
  if (!raw) {
    return {
      ok: false,
      error:
        type === "mobile"
          ? "Please enter a mobile number."
          : "Please enter an email address.",
    };
  }
  if (type === "mobile") {
    const normalized = normalizeMobile(raw);
    if (E164_MOBILE_REGEX.test(normalized)) {
      return { ok: true, value: normalized };
    }
    return {
      ok: false,
      error:
        "Please enter a valid 10-digit mobile number (or with country code, e.g. +919876543210).",
    };
  }
  if (!EMAIL_REGEX.test(raw)) {
    return { ok: false, error: "Please enter a valid email address." };
  }
  return { ok: true, value: raw };
};

const Settings = ({
  wrapperClass,
  addMfaDevice,
  getMfaDevices,
  removeMfaDevice,
  enableMFA,
  disableMFA,
  prepareMfaDeviceVerification,
  setAlert: showAlert,
  qrCodeUrl,
  mfaDeviceName,
  tenantInfo,
  generateUpdateProfileOtp: sendUpdateProfileOtp,
  validateUpdateProfileOtp: confirmUpdateProfileOtp,
}) => {
  const userInfo = useContext(UserContext);
  const mfaThemeClassName =
    tenantInfo?.primaryColor && tenantInfo?.secondaryColor
      ? `${tenantInfo.primaryColor}-theme ${tenantInfo.secondaryColor}-theme-secondary`
      : "dark-blue-theme lime-theme-secondary";
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [open, setOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [openContactDialog, setOpenContactDialog] = useState(false);
  const [contactFlowMode, setContactFlowMode] = useState("verify"); // add | verify
  const [contactType, setContactType] = useState("mobile"); // mobile | email
  const [contactValue, setContactValue] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpFlowLoading, setOtpFlowLoading] = useState(false);
  const [mfaDevices, setMfaDevices] = useState([]);
  const [mfaDevicesLoading, setMfaDevicesLoading] = useState(false);
  const [deviceName, setDeviceName] = useState("");
  const [deviceNameError, setDeviceNameError] = useState("");
  const [addDeviceLoading, setAddDeviceLoading] = useState(false);
  const [removeDeviceName, setRemoveDeviceName] = useState("");
  const [removeLoading, setRemoveLoading] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaToggleLoading, setMfaToggleLoading] = useState(false);
  const [openDisableMfaDialog, setOpenDisableMfaDialog] = useState(false);
  const [verifyDeviceLoading, setVerifyDeviceLoading] = useState(false);
  const [openMfaEnrollmentDialog, setOpenMfaEnrollmentDialog] = useState(false);

  const handleOpenChangePassword = (event) => {
    event?.currentTarget?.blur?.();
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleOpenConfirmationDialog = () => {
    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setDeviceName("");
    setDeviceNameError("");
    setOpenConfirmationDialog(true);
  };

  const handleCloseConfirmationDialog = () => {
    if (addDeviceLoading) return;
    setOpenConfirmationDialog(false);
    setDeviceName("");
    setDeviceNameError("");
  };

  const fetchMfaDevices = useCallback(async () => {
    try {
      setMfaDevicesLoading(true);
      const devices = await getMfaDevices();
      const list = Array.isArray(devices) ? devices : [];
      setMfaDevices(list);
      return list;
    } catch (err) {
      setMfaDevices([]);
      return [];
    } finally {
      setMfaDevicesLoading(false);
    }
  }, [getMfaDevices]);

  const hasVerifiedMfaDevice = useMemo(
    () => mfaDevices.some((device) => Boolean(device?.verified)),
    [mfaDevices]
  );

  const disableMfaWhenNoVerifiedDevice = useCallback(
    async (devices, currentlyEnabled) => {
      const hasVerified = (devices || []).some((device) => Boolean(device?.verified));
      if (!currentlyEnabled || hasVerified) {
        return false;
      }
      try {
        setMfaToggleLoading(true);
        await disableMFA({ silent: true });
        setMfaEnabled(false);
        setProfileData((prev) => (prev ? { ...prev, mfaEnabled: false } : prev));
        showAlert(customConfig.mfaAutoDisabledNoVerifiedDevices, "warning");
        return true;
      } catch (err) {
        // Alert already shown in action
        return false;
      } finally {
        setMfaToggleLoading(false);
      }
    },
    [disableMFA, showAlert]
  );

  const fetchMfaStatus = useCallback(async () => {
    try {
      const iamURL = env.GET_IAM_API_URL();
      const config = {
        headers: {
          "Content-Type": "application/json",
          Tenantid: env.GET_TENANT_ID(),
        },
      };
      const response = await axios.get(`${iamURL}/api/identity/v1/user/profile`, config);
      const data = response?.data || {};
      const enabled = Boolean(data.mfaEnabled);
      setMfaEnabled(enabled);
      setProfileData((prev) => ({ ...(prev || {}), ...data }));
      return enabled;
    } catch (err) {
      // Keep current toggle state; profile errors are shown when user opens Profile
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const syncMfaState = async () => {
      const [devices, enabled] = await Promise.all([
        fetchMfaDevices(),
        fetchMfaStatus(),
      ]);
      if (cancelled || enabled == null) return;
      await disableMfaWhenNoVerifiedDevice(devices, enabled);
    };
    syncMfaState();
    return () => {
      cancelled = true;
    };
  }, [fetchMfaDevices, fetchMfaStatus, disableMfaWhenNoVerifiedDevice]);

  useEffect(() => {
    if (!qrCodeUrl) return;
    setOpenConfirmationDialog(false);
    setDeviceName("");
    setDeviceNameError("");
    setOpenMfaEnrollmentDialog(true);
  }, [qrCodeUrl]);

  const handleMfaEnrollmentSyncSuccess = async () => {
    setOpenMfaEnrollmentDialog(false);
    await fetchMfaDevices();
  };

  const handleOpenChangeDeviceDialog = async () => {
    const trimmedName = (deviceName || "").trim();
    if (!trimmedName) {
      setDeviceNameError(customConfig.mfaDeviceNameRequired);
      return;
    }

    const isDuplicate = mfaDevices.some(
      (device) =>
        String(device?.deviceName || "").trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (isDuplicate) {
      setDeviceNameError(customConfig.mfaDeviceNameDuplicate);
      return;
    }

    try {
      setAddDeviceLoading(true);
      setDeviceNameError("");
      await addMfaDevice(trimmedName);
    } finally {
      setAddDeviceLoading(false);
    }
  };

  const handleConfirmRemoveDevice = async () => {
    if (!removeDeviceName) return;
    try {
      setRemoveLoading(true);
      await removeMfaDevice(removeDeviceName);
      setRemoveDeviceName("");
      const devices = await fetchMfaDevices();
      // Case 1: last verified device removed → auto-disable MFA
      await disableMfaWhenNoVerifiedDevice(devices, mfaEnabled);
    } catch (err) {
      // Alert already shown in action
    } finally {
      setRemoveLoading(false);
    }
  };

  const openMfaVerifyDialog = async (name) => {
    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    const trimmedName = (name || "").trim();
    if (!trimmedName || verifyDeviceLoading) return;

    try {
      setVerifyDeviceLoading(true);
      await prepareMfaDeviceVerification(trimmedName);
    } finally {
      setVerifyDeviceLoading(false);
    }
  };

  const handleMfaToggle = () => {
    if (mfaToggleLoading) return;
    if (mfaEnabled) {
      setOpenDisableMfaDialog(true);
      return;
    }
    // Case 2: block enable when there is no verified device (none, or only unverified)
    if (!hasVerifiedMfaDevice) {
      showAlert(customConfig.mfaEnableRequiresVerifiedDevice, "danger");
      return;
    }
    handleEnableMfa();
  };

  const handleEnableMfa = async () => {
    if (!hasVerifiedMfaDevice) {
      showAlert(customConfig.mfaEnableRequiresVerifiedDevice, "danger");
      return;
    }
    try {
      setMfaToggleLoading(true);
      await enableMFA();
      setMfaEnabled(true);
      setProfileData((prev) => (prev ? { ...prev, mfaEnabled: true } : prev));
    } catch (err) {
      // Alert already shown in action
    } finally {
      setMfaToggleLoading(false);
    }
  };

  const handleConfirmDisableMfa = async () => {
    try {
      setMfaToggleLoading(true);
      await disableMFA();
      setMfaEnabled(false);
      setProfileData((prev) => (prev ? { ...prev, mfaEnabled: false } : prev));
      setOpenDisableMfaDialog(false);
    } catch (err) {
      // Alert already shown in action
    } finally {
      setMfaToggleLoading(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
  };

  const getDisplayValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
  };

  const renderRoleChips = (roles) => {
    if (!Array.isArray(roles) || roles.length === 0) {
      return <Typography variant="body2">-</Typography>;
    }

    return (
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
        {roles.map((role) => (
          <Chip
            key={String(role)}
            label={String(role)}
            size="small"
            color="primary"
            variant="outlined"
          />
        ))}
      </Box>
    );
  };

  const extractErrorMessage = (err) => {
    if (!err) return "Something went wrong. Please try again.";
    if (typeof err === "string") return err;
    if (err.response?.data) {
      const d = err.response.data;
      if (typeof d === "string") return d;
      if (d.message) return d.message;
      if (d.error) return d.error;
    }
    return err.message || "Something went wrong. Please try again.";
  };

  const fetchUserProfile = async (force = false) => {
    if (!force && (profileData || profileLoading)) {
      return;
    }
    const silent = Boolean(force && profileData);
    try {
      if (!silent) {
        setProfileLoading(true);
      }
      const iamURL = env.GET_IAM_API_URL();
      const tenantId = env.GET_TENANT_ID();
      const config = {
        headers: {
          "Content-Type": "application/json",
          Tenantid: tenantId,
        },
      };
      const response = await axios.get(`${iamURL}/api/identity/v1/user/profile`, config);
      const data = response?.data || {};
      setProfileData(data);
      setMfaEnabled(Boolean(data.mfaEnabled));
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to fetch user profile";
      showAlert(msg, "danger");
    } finally {
      if (!silent) {
        setProfileLoading(false);
      }
    }
  };

  const openContactPopup = (type, mode, value = "") => {
    setContactFlowMode(mode);
    setContactType(type);
    setContactValue(value || "");
    setOtp("");
    setOtpSent(false);
    setOpenContactDialog(true);
  };

  const openAddContact = (type) => openContactPopup(type, "add", "");

  const openVerifyContact = (type, value) => {
    if (!value) {
      showAlert(`No ${type} available to verify`, "danger");
      return;
    }
    openContactPopup(type, "verify", String(value));
  };

  const closeContactDialog = (force = false) => {
    if (!force && otpFlowLoading) return;
    setOpenContactDialog(false);
    setContactFlowMode("verify");
    setContactType("mobile");
    setContactValue("");
    setOtpSent(false);
    setOtp("");
  };

  const handleSendContactOtp = async () => {
    const result = validateContactInput(contactType, contactValue);
    if (!result.ok) {
      showAlert(result.error, "danger");
      return;
    }

    try {
      setOtpFlowLoading(true);
      setContactValue(result.value);
      const payload =
        contactType === "mobile"
          ? { mobile: result.value }
          : { email: result.value };
      await sendUpdateProfileOtp(payload);
      setOtpSent(true);
      setOtp("");
      showAlert("OTP sent successfully.", "success");
    } catch (err) {
      showAlert(extractErrorMessage(err), "danger");
    } finally {
      setOtpFlowLoading(false);
    }
  };

  const handleVerifyContactOtp = async () => {
    const result = validateContactInput(contactType, contactValue);
    if (!result.ok) {
      showAlert(result.error, "danger");
      return;
    }
    const code = (otp || "").trim();
    if (!code) {
      showAlert("Please enter the OTP.", "danger");
      return;
    }

    try {
      setOtpFlowLoading(true);
      const payload =
        contactType === "mobile"
          ? { mobile: result.value, otp: code }
          : { email: result.value, otp: code };
      await confirmUpdateProfileOtp(payload);
      const successMsg =
        contactFlowMode === "add"
          ? `${contactType === "mobile" ? "Mobile" : "Email"} added successfully.`
          : "Verification completed successfully.";
      showAlert(successMsg, "success");
      closeContactDialog(true);
      await fetchUserProfile(true);
    } catch (err) {
      showAlert(extractErrorMessage(err), "danger");
    } finally {
      setOtpFlowLoading(false);
    }
  };

  const contactDialogTitle =
    contactFlowMode === "add"
      ? `Add ${contactType === "mobile" ? "Mobile" : "Email"}`
      : `${contactType === "mobile" ? "Mobile" : "Email"} Verification`;

  const renderContactField = (label, type, value, verified) => {
    const present = hasContactValue(value);
    return (
      <>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2">{getContactDisplay(value)}</Typography>
          {present && verified && (
            <CheckCircleRoundedIcon
              sx={{ color: "success.main", fontSize: 18 }}
              titleAccess="Verified"
            />
          )}
        </Box>
        {!present && (
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => openAddContact(type)}
            sx={CONTACT_ACTION_LINK_SX}
          >
            Add
          </Link>
        )}
        {present && !verified && (
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => openVerifyContact(type, value)}
            sx={CONTACT_ACTION_LINK_SX}
          >
            Verify
          </Link>
        )}
      </>
    );
  };

  return (
    <Fragment>
      <ResponsiveContentWrapper className={wrapperClass || ""}>
        <Box sx={{ width: "100%", minWidth: 0 }}>
          {userInfo.level > 1 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, boxShadow: 2, borderRadius: 1 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Change Password
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Need to update your password?
                  </Typography>
                </Box>
                <AppButton variant="primary" size="medium" onClick={handleOpenChangePassword} sx={{ alignSelf: { xs: "stretch", sm: "center" } }}>
                  Change Password
                </AppButton>
              </Stack>
            </Paper>
          )}
          <ChangePassword open={open} onClose={handleClose} />
          <Paper variant="outlined" sx={{ p: 2, mb: 2, boxShadow: 2, borderRadius: 1 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Two Step Authentication
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {mfaEnabled ? "Enabled" : "Disabled"}
                  </Typography>
                  <SwitchButton
                    id="mfa-account-toggle"
                    checked={mfaEnabled}
                    disabled={
                      mfaToggleLoading ||
                      (!mfaEnabled && !hasVerifiedMfaDevice)
                    }
                    placement="right"
                    tooltip={
                      mfaEnabled
                        ? "Disable MFA for account"
                        : hasVerifiedMfaDevice
                          ? "Enable MFA for account"
                          : customConfig.mfaEnableRequiresVerifiedDeviceShort
                    }
                    onChange={handleMfaToggle}
                  />
                  {mfaToggleLoading && <CircularProgress size={16} />}
                </Stack>
              </Box>
              <AppButton
                variant="primary"
                size="medium"
                onClick={handleOpenConfirmationDialog}
                sx={{ alignSelf: { xs: "stretch", sm: "flex-end" } }}
              >
                Add Device
              </AppButton>
            </Stack>
            {mfaDevicesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            ) : mfaDevices.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No MFA devices registered.
              </Typography>
            ) : (
              <TableContainer
                sx={{
                  // Scroll only when more than 3 devices; 3 rows fit fully with no scrollbar
                  maxHeight: mfaDevices.length > 3 ? 184 : "none",
                  overflowY: mfaDevices.length > 3 ? "auto" : "visible",
                  ...(mfaDevices.length > 3
                    ? {
                        "& .MuiTableHead-root .MuiTableCell-root": {
                          py: 1,
                          backgroundColor: "background.paper",
                        },
                        "& .MuiTableBody-root .MuiTableRow-root": {
                          height: 48,
                        },
                        "& .MuiTableBody-root .MuiTableCell-root": {
                          py: 0.75,
                        },
                      }
                    : {}),
                }}
              >
                <Table size="small" stickyHeader={mfaDevices.length > 3}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Device Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created At</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mfaDevices.map((device) => (
                      <TableRow key={device.deviceName}>
                        <TableCell>{device.deviceName || "-"}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                            <Chip
                              size="small"
                              label={device.verified ? "Verified" : "Not Verified"}
                              color={device.verified ? "success" : "default"}
                              variant={device.verified ? "filled" : "outlined"}
                            />
                            {!device.verified && (
                              <Link
                                component="button"
                                type="button"
                                variant="body2"
                                onClick={() => openMfaVerifyDialog(device.deviceName)}
                                disabled={verifyDeviceLoading}
                                sx={{ fontSize: 12 }}
                              >
                                {verifyDeviceLoading ? "Loading..." : "Verify"}
                              </Link>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{formatDateTime(device.createdAt)}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            variant="danger"
                            size="small"
                            icon={<DeleteIcon />}
                            tooltip="Remove device"
                            onClick={() => setRemoveDeviceName(device.deviceName)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, boxShadow: 2, borderRadius: 1 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                User Information
              </Typography>
              <AppButton variant="primary" size="medium" onClick={fetchUserProfile} sx={{ alignSelf: { xs: "stretch", sm: "flex-end" } }}>
                {profileLoading ? "Loading..." : "Profile"}
              </AppButton>
            </Stack>
            {profileData && (
              <Box sx={{ mt: 2 }}>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">Username</Typography>
                    <Typography variant="body2">{getDisplayValue(profileData.username)}</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    {renderContactField(
                      "Mobile",
                      "mobile",
                      profileData.mobile,
                      Boolean(profileData.mobileVerified)
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    {renderContactField(
                      "Email",
                      "email",
                      profileData.email,
                      Boolean(profileData.emailVerified)
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">MFA Enabled</Typography>
                    <Typography variant="body2">{getDisplayValue(profileData.mfaEnabled)}</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">Last Logged In</Typography>
                    <Typography variant="body2">{formatDateTime(profileData.lastLoggedInTs)}</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">Creation Date</Typography>
                    <Typography variant="body2">{formatDateTime(profileData.creationDate)}</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">Roles</Typography>
                    {renderRoleChips(profileData.roles)}
                  </Grid2>
                </Grid2>
              </Box>
            )}
          </Paper>
          <Alert />
        </Box>
      </ResponsiveContentWrapper>
      <Dialog
        open={openConfirmationDialog}
        onClose={handleCloseConfirmationDialog}
        title="Add Two-Step Authentication Device"
        maxWidth="sm"
        closeOnBackdropClick={!addDeviceLoading}
        closeOnEscapeKeyDown={!addDeviceLoading}
        actions={[
          {
            label: "Cancel",
            variant: "ghost",
            onClick: handleCloseConfirmationDialog,
            disabled: addDeviceLoading,
          },
          {
            label: addDeviceLoading ? "Adding..." : "Ok",
            variant: "primary",
            onClick: handleOpenChangeDeviceDialog,
            disabled: addDeviceLoading,
            loading: addDeviceLoading,
          },
        ]}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Enter a unique device name, then scan the QR code with your authenticator app.
          </Typography>
          <FormInput
            label="Device Name *"
            name="deviceName"
            value={deviceName}
            onChange={(e) => {
              setDeviceName(e.target.value);
              if (deviceNameError) setDeviceNameError("");
            }}
            fullWidth
            size="small"
            error={Boolean(deviceNameError)}
            helperText={deviceNameError || ""}
            touched="true"
            disabled={addDeviceLoading}
          />
        </Box>
      </Dialog>
      {openMfaEnrollmentDialog &&
        qrCodeUrl &&
        createPortal(
          <Box
            className={mfaThemeClassName}
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 2400,
            }}
          >
            <MfaQrCode
              key={qrCodeUrl || mfaDeviceName || "mfa-enrollment"}
              tenantData={tenantInfo}
              deviceName={mfaDeviceName}
              skipReload
              onSyncSuccess={handleMfaEnrollmentSyncSuccess}
            />
          </Box>,
          document.body
        )}
      <Dialog
        open={Boolean(removeDeviceName)}
        onClose={() => {
          if (!removeLoading) setRemoveDeviceName("");
        }}
        title="Remove MFA Device?"
        maxWidth="sm"
        closeOnBackdropClick={!removeLoading}
        closeOnEscapeKeyDown={!removeLoading}
        actions={[
          {
            label: "Cancel",
            variant: "ghost",
            onClick: () => setRemoveDeviceName(""),
            disabled: removeLoading,
          },
          {
            label: removeLoading ? "Removing..." : "Remove",
            variant: "primary",
            onClick: handleConfirmRemoveDevice,
            disabled: removeLoading,
            loading: removeLoading,
          },
        ]}
      >
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to remove device &quot;{removeDeviceName}&quot;? This cannot be undone.
        </Typography>
      </Dialog>
      <Dialog
        open={openDisableMfaDialog}
        onClose={() => {
          if (!mfaToggleLoading) setOpenDisableMfaDialog(false);
        }}
        title="Disable Two-Step Authentication?"
        maxWidth="sm"
        closeOnBackdropClick={!mfaToggleLoading}
        closeOnEscapeKeyDown={!mfaToggleLoading}
        actions={[
          {
            label: "Cancel",
            variant: "ghost",
            onClick: () => setOpenDisableMfaDialog(false),
            disabled: mfaToggleLoading,
          },
          {
            label: mfaToggleLoading ? "Disabling..." : "Disable",
            variant: "primary",
            onClick: handleConfirmDisableMfa,
            disabled: mfaToggleLoading,
            loading: mfaToggleLoading,
          },
        ]}
      >
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to disable Two-Step Authentication for your account? You can enable it again later.
        </Typography>
      </Dialog>
      <Dialog
        open={openContactDialog}
        onClose={closeContactDialog}
        title={contactDialogTitle}
        maxWidth="xs"
        fullWidth
        closeOnBackdropClick={!otpFlowLoading}
        closeOnEscapeKeyDown={!otpFlowLoading}
        actions={
          !otpSent
            ? [
                {
                  label: "Cancel",
                  variant: "ghost",
                  onClick: closeContactDialog,
                  disabled: otpFlowLoading,
                },
                {
                  label: otpFlowLoading ? "Sending..." : "Send OTP",
                  variant: "primary",
                  onClick: handleSendContactOtp,
                  disabled: otpFlowLoading,
                  loading: otpFlowLoading,
                },
              ]
            : [
                {
                  label: "Cancel",
                  variant: "ghost",
                  onClick: closeContactDialog,
                  disabled: otpFlowLoading,
                },
                {
                  label: otpFlowLoading ? "Verifying..." : "Verify OTP",
                  variant: "primary",
                  onClick: handleVerifyContactOtp,
                  disabled: otpFlowLoading,
                  loading: otpFlowLoading,
                },
              ]
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
          <FormInput
            label={contactType === "mobile" ? "Mobile Number" : "Email"}
            name="profileContactValue"
            value={contactValue}
            onChange={(e) => setContactValue(e.target.value)}
            placeholder={
              contactType === "mobile"
                ? "9876543210 or +919876543210"
                : "name@example.com"
            }
            fullWidth
            size="small"
            disabled={otpSent || otpFlowLoading}
          />
          {otpSent && (
            <FormInput
              label="Enter OTP"
              name="profileContactOtp"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              fullWidth
              size="small"
              disabled={otpFlowLoading}
            />
          )}
        </Box>
      </Dialog>
    </Fragment>
  );
};

Settings.propTypes = {
  addMfaDevice: PropTypes.func.isRequired,
  getMfaDevices: PropTypes.func.isRequired,
  removeMfaDevice: PropTypes.func.isRequired,
  enableMFA: PropTypes.func.isRequired,
  disableMFA: PropTypes.func.isRequired,
  prepareMfaDeviceVerification: PropTypes.func.isRequired,
  qrCodeUrl: PropTypes.string,
  mfaDeviceName: PropTypes.string,
  tenantInfo: PropTypes.object,
  setAlert: PropTypes.func.isRequired,
  generateUpdateProfileOtp: PropTypes.func.isRequired,
  validateUpdateProfileOtp: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  qrCodeUrl: state.auth.qrCodeUrl,
  mfaDeviceName: state.auth.mfaDeviceName,
  tenantInfo: state.tenant.tenantInfo,
});

const mapDispatchToProps = (dispatch) => ({
  addMfaDevice: (deviceName) => dispatch(addMfaDevice(deviceName)),
  getMfaDevices: () => dispatch(getMfaDevices()),
  removeMfaDevice: (deviceName) => dispatch(removeMfaDevice(deviceName)),
  enableMFA: () => dispatch(enableMFA()),
  disableMFA: (options) => dispatch(disableMFA(options)),
  prepareMfaDeviceVerification: (deviceName) =>
    dispatch(prepareMfaDeviceVerification(deviceName)),
  setAlert: (msg, type) => dispatch(setAlert(msg, type)),
  generateUpdateProfileOtp: (payload) => dispatch(generateUpdateProfileOtp(payload)),
  validateUpdateProfileOtp: (payload) => dispatch(validateUpdateProfileOtp(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Settings);
